import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Settings, X, Cookie } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {!showPreferences && (
          <motion.div
            key="cookie-banner"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'fixed bottom-4 left-4 right-4 md:right-auto md:max-w-md',
              'bg-white shadow-2xl',
              'border border-brand-dark/10',
              'border-t-4 border-t-brand-red',
              'rounded-lg overflow-hidden'
            )}
            style={{ zIndex: 9999 }}
            role="dialog"
            aria-label={t('cookie.title', 'Cookie consent')}
          >
            <div className="p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-md bg-brand-red/10 text-brand-red flex items-center justify-center">
                  <Cookie className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-brand-red font-semibold tracking-[0.2em] text-[10px] uppercase mb-0.5">
                    {t('cookie.tagline', 'Privacy')}
                  </p>
                  <h2 className="font-bold text-brand-dark text-base leading-tight">
                    {t('cookie.title', 'Rispettiamo la tua privacy')}
                  </h2>
                </div>
              </div>

              <p className="text-sm text-brand-text/80 leading-relaxed mb-4">
                {t('cookie.message')}{' '}
                <LocalizedLink
                  to="privacy"
                  className="font-semibold text-brand-dark underline decoration-brand-red decoration-2 underline-offset-2 hover:text-brand-red transition-colors"
                >
                  {t('cookie.learnMore')}
                </LocalizedLink>
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <button
                  onClick={rejectAll}
                  className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-md transition-colors bg-white border border-brand-dark/20 text-brand-dark hover:bg-brand-dark hover:text-white hover:border-brand-dark"
                >
                  {t('cookie.rejectAll', 'Rifiuta')}
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-md transition-colors bg-brand-red text-white hover:bg-brand-red-dark"
                >
                  {t('cookie.acceptAll', 'Accetta tutti')}
                </button>
              </div>

              <button
                onClick={() => setShowPreferences(true)}
                className="mt-3 flex items-center justify-center gap-1.5 w-full text-xs font-medium tracking-wide uppercase text-brand-text/60 hover:text-brand-red transition-colors"
              >
                <Settings className="h-3.5 w-3.5" />
                {t('cookie.customize', 'Personalizza')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {showPreferences && (
          <motion.div
            key="cookie-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 flex items-center justify-center bg-brand-dark/70 backdrop-blur-sm p-4"
            style={{ zIndex: 10000 }}
            onClick={() => setShowPreferences(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                'bg-white max-w-md w-full max-h-[90vh] overflow-y-auto',
                'border border-brand-dark/10',
                'border-t-4 border-t-brand-red',
                'rounded-lg shadow-2xl'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-1 gap-3">
                  <div>
                    <p className="text-brand-red font-semibold tracking-[0.2em] text-[10px] uppercase mb-1">
                      {t('cookie.tagline', 'Privacy')}
                    </p>
                    <h2 className="text-xl font-bold text-brand-dark leading-tight">
                      {t('cookie.preferencesTitle', 'Preferenze Cookie')}
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-1.5 -m-1.5 text-brand-text/60 hover:text-brand-red hover:bg-brand-red/5 rounded-md transition-colors"
                    aria-label={t('aria.close', 'Chiudi')}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <p className="text-sm text-brand-text/80 leading-relaxed mb-6">
                  {t(
                    'cookie.preferencesDescription',
                    'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.'
                  )}
                </p>

                <div className="space-y-2">
                  {/* Necessary */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-[#F9F6F1] rounded-md border border-brand-dark/5">
                    <div>
                      <p className="font-semibold text-sm text-brand-dark">
                        {t('cookie.necessary', 'Necessari')}
                      </p>
                      <p className="text-xs text-brand-text/70 mt-1 leading-relaxed">
                        {t(
                          'cookie.necessaryDesc',
                          'Essenziali per il funzionamento del sito.'
                        )}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-[10px] tracking-[0.15em] uppercase font-semibold text-brand-red bg-brand-red/10 px-2 py-1 rounded">
                      {t('cookie.alwaysOn', 'Sempre attivi')}
                    </span>
                  </div>

                  {/* Analytics */}
                  <ToggleRow
                    label={t('cookie.analytics', 'Analitici')}
                    description={t(
                      'cookie.analyticsDesc',
                      'Ci aiutano a capire come usi il sito.'
                    )}
                    checked={tempPrefs.analytics}
                    onChange={() =>
                      setTempPrefs((p) => ({ ...p, analytics: !p.analytics }))
                    }
                  />

                  {/* Marketing */}
                  <ToggleRow
                    label={t('cookie.marketing', 'Marketing')}
                    description={t(
                      'cookie.marketingDesc',
                      'Per contenuti e pubblicità personalizzati.'
                    )}
                    checked={tempPrefs.marketing}
                    onChange={() =>
                      setTempPrefs((p) => ({ ...p, marketing: !p.marketing }))
                    }
                  />
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="py-2.5 px-5 text-sm font-semibold text-brand-text/70 hover:text-brand-dark hover:bg-brand-gray rounded-md transition-colors"
                  >
                    {t('cookie.cancel', 'Annulla')}
                  </button>
                  <button
                    onClick={() => {
                      savePreferences(tempPrefs)
                      setShowPreferences(false)
                    }}
                    className="py-2.5 px-5 text-sm font-semibold rounded-md transition-colors bg-brand-red text-white hover:bg-brand-red-dark"
                  >
                    {t('cookie.savePreferences', 'Salva preferenze')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

interface ToggleRowProps {
  label: string
  description: string
  checked: boolean
  onChange: () => void
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 bg-[#F9F6F1] rounded-md border border-brand-dark/5">
      <div className="flex-1">
        <p className="font-semibold text-sm text-brand-dark">{label}</p>
        <p className="text-xs text-brand-text/70 mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
        className={cn(
          'relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors mt-0.5',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
          checked ? 'bg-brand-red' : 'bg-brand-dark/20'
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0.5'
          )}
        />
      </button>
    </div>
  )
}
